import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTenantConfig } from '../context/TenantConfigContext'
import { fetchProposalPublic, loadVehiclesForProposal, postProposalVehicleLike } from '../api/publicApi'
import {
  resolveTenantUuid,
  trackProposalView,
  trackProposalVehicleView,
} from '../api/tracking'
import Logo from '../components/headers/Logo'
import VehicleImagePlaceholder from '../components/VehicleImagePlaceholder'
import './ProposalPage.css'

function advisorInitials(name) {
  const s = String(name || 'A').replace(/^\[[^\]]+\]\s*/i, '').trim()
  const parts = s.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
  }
  return s.slice(0, 2).toUpperCase() || '—'
}

function formatCardPrice(vehicle) {
  if (vehicle.priceLabel) {
    const m = String(vehicle.priceLabel).match(/^([A-Z]{3})\s+(.+)$/i)
    if (m) return { currency: m[1].toUpperCase(), amount: m[2] }
  }
  const currency = vehicle.currency || 'USD'
  const amount = new Intl.NumberFormat('es-UY', { maximumFractionDigits: 0 }).format(vehicle.price ?? 0)
  return { currency, amount }
}

function getProposalCardLines(vehicle) {
  const line1 =
    vehicle.titleLine1 ||
    [vehicle.brand, vehicle.model].filter(Boolean).join(' ').trim() ||
    vehicle.title ||
    'Vehículo'
  const line2 = vehicle.titleLine2 || vehicle.version || ''
  const year = vehicle.year != null && vehicle.year !== '' ? String(vehicle.year) : null
  const km = vehicle.mileage != null && vehicle.mileage !== '' ? Number(vehicle.mileage) : null
  return { line1, line2, year, km }
}

function IconWhatsApp() {
  return (
    <svg className="proposal-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.899a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

function IconMail() {
  return (
    <svg className="proposal-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <path d="M22 6l-10 7L2 6" />
    </svg>
  )
}

function IconHeart({ active }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden
    >
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  )
}

function IconSpecCal() {
  return (
    <svg className="proposal-spec__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  )
}

function IconSpecOdo() {
  return (
    <svg className="proposal-spec__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v2M12 16v-2M7 12h2M16 12h-2" />
    </svg>
  )
}

function IconSpecGear() {
  return (
    <svg className="proposal-spec__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  )
}

function IconSpecFuel() {
  return (
    <svg className="proposal-spec__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M3 22h12M3 2v8a4 4 0 004 4h.5" />
      <path d="M7 2h8l3 3v3a2 2 0 01-2 2h-1M7 2v4M17 5h2a1 1 0 011 1v1" />
    </svg>
  )
}

function vehicleLineId(line, vehicle) {
  const a = line && typeof line.vehicleUuid === 'string' ? line.vehicleUuid.trim() : ''
  if (a) return a
  return String(vehicle?.uuid || '')
}

function resolveLiked(likeOverrides, line, vehicle) {
  const id = vehicleLineId(line, vehicle)
  if (!id) return false
  if (Object.prototype.hasOwnProperty.call(likeOverrides, id)) {
    return likeOverrides[id] === true
  }
  return line?.customerLiked === true
}

export default function ProposalPage() {
  const { uuid } = useParams()
  const { t } = useTranslation()
  const { config } = useTenantConfig()
  const [proposal, setProposal] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [likeOverrides, setLikeOverrides] = useState(() => ({}))
  const [likePending, setLikePending] = useState(() => ({}))
  const [likeError, setLikeError] = useState(() => ({}))

  const brandName = useMemo(() => {
    return proposal?.tenantName || config?.brand?.name || t('proposal.defaultBrand')
  }, [proposal, config, t])

  const showAgent = proposal?.showAgent !== false

  const advisorName = useMemo(() => {
    if (showAgent && proposal?.agent) {
      const full = [proposal.agent.name, proposal.agent.lastname].filter(Boolean).join(' ').trim()
      if (full) return full
    }
    const advisor = config?.advisor || config?.contact
    return advisor?.name || config?.publicInfo?.firstPhoneNumber || brandName
  }, [proposal, config, brandName, showAgent])

  const advisorPhone = useMemo(() => {
    const fromProposal = showAgent ? proposal?.agent?.phone : ''
    const fromConfig =
      config?.advisor?.phone || config?.contact?.phone || config?.contact?.whatsapp || ''
    return String(fromProposal || fromConfig).replace(/\s/g, '')
  }, [proposal, config, showAgent])

  const advisorEmail = useMemo(() => {
    const fromProposal = showAgent ? proposal?.agent?.email : ''
    return fromProposal || config?.advisor?.email || config?.contact?.email || ''
  }, [proposal, config, showAgent])

  const advisorPhoto = config?.advisor?.photo
  const initials = useMemo(() => advisorInitials(advisorName), [advisorName])
  const year = new Date().getFullYear()

  const logoBrand = useMemo(
    () => ({
      name: brandName,
      logo: config?.brand?.logo,
      tagline: config?.brand?.tagline,
    }),
    [brandName, config],
  )

  useEffect(() => {
    if (!uuid) {
      setError(t('proposal.errorMissingId'))
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    setProposal(null)
    setRows([])
    setLikeOverrides({})
    setLikeError({})
    setLikePending({})
    void (async () => {
      try {
        const p = await fetchProposalPublic(uuid)
        if (cancelled) return
        setProposal(p)
        const loaded = await loadVehiclesForProposal(p)
        if (cancelled) return
        setRows(loaded)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : t('proposal.errorLoad'))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [uuid, t])

  useEffect(() => {
    const pName = proposal?.name?.trim()
    const b = proposal?.tenantName || config?.brand?.name
    if (pName && b) {
      document.title = `${pName} - ${b}`
    } else if (pName) {
      document.title = pName
    } else if (b) {
      document.title = t('proposal.documentTitle', { brand: b })
    }
  }, [proposal, config, t])

  useEffect(() => {
    if (!proposal?.uuid) return
    const tenantUuid = resolveTenantUuid(config, proposal)
    if (!tenantUuid) return
    trackProposalView(tenantUuid, proposal.uuid)
  }, [proposal, config])

  useEffect(() => {
    if (!proposal?.uuid || !Array.isArray(rows) || rows.length === 0) return
    const tenantUuid = resolveTenantUuid(config, proposal)
    if (!tenantUuid) return
    for (const { line, vehicle } of rows) {
      const vehicleUuid = vehicleLineId(line, vehicle)
      if (!vehicleUuid) continue
      trackProposalVehicleView(tenantUuid, proposal.uuid, vehicleUuid)
    }
  }, [proposal, rows, config])

  const introWhatsApp = advisorPhone
    ? `https://wa.me/${advisorPhone.replace(/\D/g, '')}?text=${encodeURIComponent(
        t('proposal.whatsappPreamble', { name: proposal?.name || '' }),
      )}`
    : null

  const handleVehicleLike = async (line, vehicle) => {
    const proposalId = proposal?.uuid
    const vid = vehicleLineId(line, vehicle)
    if (!proposalId || !vid) return
    const current = resolveLiked(likeOverrides, line, vehicle)
    setLikeError((e) => {
      const n = { ...e }
      delete n[vid]
      return n
    })
    setLikePending((p) => ({ ...p, [vid]: true }))
    try {
      const nextLiked = !current
      await postProposalVehicleLike(proposalId, vid, { customerLiked: nextLiked })
      setLikeOverrides((o) => ({ ...o, [vid]: nextLiked }))
    } catch (e) {
      setLikeError((prev) => ({
        ...prev,
        [vid]: e instanceof Error ? e.message : t('proposal.likeError'),
      }))
    } finally {
      setLikePending((p) => {
        const n = { ...p }
        delete n[vid]
        return n
      })
    }
  }

  if (loading) {
    return (
      <div className="proposal proposal--centered">
        <div className="proposal__state" role="status" aria-live="polite">
          <div className="proposal__spinner" aria-hidden />
          <p className="proposal__state-text">{t('proposal.loading')}</p>
        </div>
      </div>
    )
  }

  if (error || !proposal) {
    return (
      <div className="proposal proposal--centered">
        <div className="proposal__state">
          <p className="proposal__state-text proposal__state-text--error" role="alert">
            {error || t('proposal.errorLoad')}
          </p>
          <Link to="/" className="proposal__back">
            {t('proposal.backHome')}
          </Link>
        </div>
      </div>
    )
  }

  const hasContact = showAgent && (introWhatsApp || advisorEmail)

  return (
    <div className="proposal">
      <header className="proposal__topbar">
        <div className="proposal__topbar-inner">
          <Logo brand={logoBrand} className="proposal__logo" />
        </div>
      </header>

      <section className="proposal__hero" aria-labelledby="proposal-hero-title">
        <div className="proposal__hero-glow" aria-hidden />
        <div className="proposal__hero-inner">
          <span className="proposal__eyebrow">{t('proposal.eyebrow')}</span>
          <h1 id="proposal-hero-title" className="proposal__title">
            {proposal.name}
          </h1>
          <p className="proposal__hero-sub">
            {rows.length > 0
              ? t('proposal.heroSub', { count: rows.length })
              : t('proposal.heroSubEmpty')}
          </p>
        </div>
      </section>

      <div className="proposal__body">
        <div className={`proposal__layout${hasContact ? '' : ' proposal__layout--solo'}`}>
          {hasContact ? (
            <aside className="proposal__aside" aria-label={t('proposal.sidebarAria')}>
              <div className="proposal__advisor">
                <div className="proposal__advisor-head">
                  <div className="proposal__avatar" aria-hidden>
                    {advisorPhoto ? <img src={advisorPhoto} alt="" /> : <span>{initials}</span>}
                  </div>
                  <h2 className="proposal__agent-name">{advisorName}</h2>
                  <p className="proposal__agent-brand">{brandName}</p>
                </div>
                <p className="proposal__intro">
                  {t('proposal.introLine1', { advisor: advisorName })}{' '}
                  <strong>{brandName}</strong>
                  {t('proposal.introLine2')}
                </p>
                <div className="proposal__contact">
                  {introWhatsApp ? (
                    <a
                      className="proposal__contact-btn proposal__contact-btn--wa"
                      href={introWhatsApp}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <IconWhatsApp />
                      {t('proposal.contactWhatsApp')}
                    </a>
                  ) : null}
                  {advisorEmail ? (
                    <a className="proposal__contact-btn proposal__contact-btn--mail" href={`mailto:${advisorEmail}`}>
                      <IconMail />
                      {t('proposal.contactEmail')}
                    </a>
                  ) : null}
                </div>
              </div>
            </aside>
          ) : null}

          <main className="proposal__main">
            {rows.length === 0 ? (
              <p className="proposal__empty">{t('proposal.emptyVehicles')}</p>
            ) : (
              <ul className="proposal__grid">
                {rows.map(({ vehicle, line }) => {
                  const detailVehicleId = String(vehicle.uuid || vehicle.id || line?.vehicleUuid || '').trim()
                  const description = vehicle.description || vehicle.raw?.description?.trim()
                  const vKey = vehicleLineId(line, vehicle)
                  const liked = resolveLiked(likeOverrides, line, vehicle)
                  const pending = Boolean(vKey && likePending[vKey])
                  const likeErr = vKey ? likeError[vKey] : null
                  const { currency, amount } = formatCardPrice(vehicle)
                  const { line1, line2, year: yearVal, km: kmVal } = getProposalCardLines(vehicle)
                  const highlighted = line?.isHighlighted === true

                  return (
                    <li
                      key={vKey || vehicle.uuid}
                      className={highlighted ? 'proposal-card proposal-card--highlighted' : 'proposal-card'}
                    >
                      <div className="proposal-card__media">
                        {vehicle.image ? (
                          <img
                            src={vehicle.image}
                            alt=""
                            className="proposal-card__image"
                            loading="lazy"
                            width={800}
                            height={500}
                          />
                        ) : (
                          <VehicleImagePlaceholder className="proposal-card__image proposal-card__image--empty" />
                        )}
                        <div className="proposal-card__badges">
                          {highlighted ? (
                            <span className="proposal-card__badge proposal-card__badge--featured">
                              {t('proposal.highlighted')}
                            </span>
                          ) : null}
                          {yearVal ? <span className="proposal-card__badge">{yearVal}</span> : null}
                          {kmVal != null ? (
                            <span className="proposal-card__badge">{kmVal.toLocaleString('es-UY')} km</span>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className={
                            liked
                              ? 'proposal-card__like proposal-card__like--active'
                              : 'proposal-card__like'
                          }
                          onClick={() => void handleVehicleLike(line, vehicle)}
                          disabled={pending}
                          aria-pressed={liked}
                          aria-label={liked ? t('proposal.likeAriaRemove') : t('proposal.likeAriaAdd')}
                          aria-busy={pending}
                        >
                          <IconHeart active={liked} />
                        </button>
                      </div>

                      <div className="proposal-card__body">
                        <h3 className="proposal-card__title">{line1}</h3>
                        {line2 ? <p className="proposal-card__subtitle">{line2}</p> : null}
                        {description ? <p className="proposal-card__desc">{description}</p> : null}
                        <div className="proposal-card__specs" aria-label={t('proposal.specs')}>
                          {yearVal ? (
                            <span className="proposal-card__spec">
                              <IconSpecCal />
                              {yearVal}
                            </span>
                          ) : null}
                          {kmVal != null ? (
                            <span className="proposal-card__spec">
                              <IconSpecOdo />
                              {kmVal.toLocaleString('es-UY')} km
                            </span>
                          ) : null}
                          {vehicle.transmission ? (
                            <span className="proposal-card__spec">
                              <IconSpecGear />
                              {vehicle.transmission}
                            </span>
                          ) : null}
                          {vehicle.fuel ? (
                            <span className="proposal-card__spec">
                              <IconSpecFuel />
                              {vehicle.fuel}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="proposal-card__foot">
                        <div>
                          <p className="proposal-card__price" aria-label={t('proposal.priceLabel')}>
                            <span className="proposal-card__currency">{currency}</span> {amount}
                          </p>
                          {likeErr ? (
                            <span className="proposal-card__like-err" role="status">
                              {likeErr}
                            </span>
                          ) : null}
                        </div>
                        {detailVehicleId ? (
                          <Link
                            to={`/vehiculos/${encodeURIComponent(detailVehicleId)}`}
                            className="proposal-card__cta"
                            state={{ fromProposal: uuid }}
                          >
                            {t('proposal.viewDetails')}
                            <span className="proposal-card__cta-arrow" aria-hidden>
                              →
                            </span>
                          </Link>
                        ) : (
                          <span className="proposal-card__cta proposal-card__cta--disabled" aria-disabled>
                            {t('proposal.viewDetails')}
                          </span>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </main>
        </div>
      </div>

      <footer className="proposal__footer">
        <p className="proposal__copy">{t('proposal.footerCopyright', { year, brand: brandName })}</p>
        <p className="proposal__powered">
          {t('proposal.poweredBy')}{' '}
          <a href="https://workit.app" target="_blank" rel="noopener noreferrer" className="proposal__powered-link">
            Workit
          </a>
        </p>
      </footer>
    </div>
  )
}
